import React from 'react';
import { DescriptionList, Blank, Text } from '@doda/io-react/common';
import { JobBusinessOutlineResponse } from '@doda/api-client/aspida/@types';
import { TextWithLF } from '@doda/common/src/components/TextWithLF';
import { PublicationPhotoArea } from 'components/CompanyOverview/PublicationPhotoArea';
import { Tab as TabType } from 'pages/DodaFront/View/JobSearchDetail/types/tab';
import { SectionTitle } from '../../SectionTitle';
import styles from './index.module.scss';
import { BusinessContent } from '../BusinessContent';

/**
 * CompanyOverviewのProps
 */
interface CompanyOverviewProps {
  businessOutline: JobBusinessOutlineResponse;
  marketName?: string;
  isManuscriptT?: boolean;
}

/**
 * 写真エリアの useTab に必要なProps
 */
export interface PhotoAreaProps {
  jid: string;
  initialTab: TabType;
  wcnsId: string;
  isApplied: boolean;
}

/** 会社概要 */
export const CompanyOverview: React.FC<
  CompanyOverviewProps & Partial<PhotoAreaProps>
> = ({
  businessOutline,
  marketName = '',
  isManuscriptT = false,
  jid,
  initialTab,
  wcnsId,
  isApplied,
}) => {
  const listData = isManuscriptT
    ? generateListDataForManuscriptT(
        businessOutline,
        marketName,
        jid,
        initialTab,
        wcnsId,
        isApplied
      )
    : generateListData(businessOutline, marketName);

  return (
    <div className={styles['jobSearchDetail-companyOverview']}>
      <SectionTitle mb="M">会社概要</SectionTitle>
      <DescriptionList
        listData={listData}
        type="column2"
        column2dataBgColor="gray"
      />
    </div>
  );
};
/** 概要リスト作成処理 */
const generateListData = (
  businessOutline: JobBusinessOutlineResponse,
  marketName: string
) => {
  const {
    businessContent = '',
    address = '',
    establishmentYear = '',
    establish = '',
    representative = '',
    employNum = '',
    capital = '',
    sales = '',
    averageAge = '',
    averageAgeExplanation = '',
    homePageLink = '',
  } = businessOutline;

  return [
    { title: '事業概要', contents: businessContent, isWithLF: true },
    { title: '所在地', contents: address, isWithLF: true },
    {
      title: '設立',
      contents:
        establish || establishmentYear
          ? createEstablishText(establish, establishmentYear)
          : '',
      isWithLF: false,
    },
    { title: '代表者', contents: representative, isWithLF: false },
    { title: '従業員数', contents: employNum, isWithLF: false },
    { title: '市場', contents: marketName, isWithLF: false },
    { title: '資本金', contents: capital, isWithLF: false },
    { title: '売上高', contents: sales, isWithLF: true },
    {
      title: '平均年齢',
      contents:
        averageAge || averageAgeExplanation
          ? createAverageAgeText(averageAge, averageAgeExplanation)
          : '',
      isWithLF: false,
    },
    {
      title: '企業URL',
      contents: homePageLink ? (
        <HomePageLink homePageLink={homePageLink} />
      ) : (
        ''
      ),
      isWithLF: false,
    },
  ]
    .filter(({ contents }) => Boolean(contents))
    .map((item) => ({
      title: item.title,
      contents:
        item.isWithLF && typeof item.contents === 'string' ? (
          <Text size="14--articlePC">
            <TextWithLF text={item.contents} />
          </Text>
        ) : (
          item.contents
        ),
    }));
};

/** 概要リスト作成処理 T原稿 */
const generateListDataForManuscriptT = (
  businessOutline: JobBusinessOutlineResponse,
  marketName: string,
  jid?: string,
  initialTab?: TabType,
  wcnsId?: string,
  isApplied?: boolean
) => {
  const {
    businessContent = '',
    address = '',
    establishmentYear = '',
    establish = '',
    representative = '',
    employNum = '',
    capital = '',
    sales = '',
    averageAge = '',
    averageAgeExplanation = '',
    currentProfit = '',
    homePageLink = '',
  } = businessOutline;

  return [
    {
      title: '事業概要',
      contents: (
        <>
          {jid && initialTab && wcnsId && isApplied !== undefined && (
            <PublicationPhotoArea
              jid={jid}
              initialTab={initialTab}
              wcnsId={wcnsId}
              isApplied={isApplied}
            />
          )}
          <BusinessContent businessContent={businessContent} />
        </>
      ),
      isWithLF: true,
    },
    { title: '所在地', contents: address, isWithLF: true },
    {
      title: '設立',
      contents: establish || establishmentYear,
      isWithLF: false,
    },
    { title: '代表者', contents: representative, isWithLF: false },
    { title: '従業員数', contents: employNum, isWithLF: false },
    { title: '上場市場名', contents: marketName, isWithLF: false },
    { title: '資本金', contents: capital, isWithLF: false },
    { title: '売上高', contents: sales, isWithLF: true },
    { title: '経常利益', contents: currentProfit, isWithLF: false },
    {
      title: '平均年齢',
      contents:
        averageAge || averageAgeExplanation
          ? createAverageAgeText(averageAge, averageAgeExplanation)
          : '',
      isWithLF: false,
    },
    {
      title: '企業URL',
      contents: homePageLink ? (
        <HomePageLink homePageLink={homePageLink} />
      ) : (
        ''
      ),
      isWithLF: false,
    },
  ]
    .filter(({ contents }) => Boolean(contents))
    .map((item) => ({
      title: item.title,
      contents:
        item.isWithLF && typeof item.contents === 'string' ? (
          <Text size="14--articlePC">
            <TextWithLF text={item.contents} />
          </Text>
        ) : (
          item.contents
        ),
    }));
};

/**
 * 企業URLコンポーネント
 */
function HomePageLink({ homePageLink }: { homePageLink: string }) {
  return (
    <Text
      className={styles['jobSearchDetail-companyOverview__link']}
      as="a"
      href={homePageLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Text color="blue" style={{ display: 'flex', gap: '4px' }}>
        <TextWithLF text={homePageLink} />
        <Blank size="S" color="blue" />
      </Text>
    </Text>
  );
}

/**
 * 平均年齢テキストを生成する
 * @param averageAge 平均年齢
 * @param averageAgeExplanation 平均年齢補足
 * @returns 平均年齢テキスト
 */
function createAverageAgeText(
  averageAge: string | undefined,
  averageAgeExplanation: string | undefined
) {
  const averageAgeText = averageAge ? `${averageAge}歳 ` : '';
  return `${averageAgeText}${averageAgeExplanation ?? ''}`;
}

/**
 * 設立年テキストを生成する
 * @param establish 設立年
 * @param establishmentYear 設立年補足
 * @returns 設立年テキスト
 */
function createEstablishText(
  establish: string | undefined,
  establishmentYear: string | undefined
) {
  const establishText = establish ? `${establish}年 ` : '';
  return `${establishText}${establishmentYear ?? ''}`;
}
